import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

export interface Profile {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
    bio: string | null;
    referred_by_id: string | null;
    role?: string | null;
    notification_preferences?: any;
}

export interface AuthState {
    user: User | null;
    profile: Profile | null;
    role: string | null;
    loading: boolean;
    fetching: boolean;
    initialize: () => void;
    refreshProfile: () => Promise<void>;
    signOut: () => Promise<void>;
}

// Singleton to track initialization status outside the store
let isInitialized = false;

// Shared promise to deduplicate active fetch requests
let activeFetchPromise: Promise<any> | null = null;

export const useAuth = create<AuthState>((set) => ({
    user: null,
    profile: null,
    role: null,
    loading: true,
    fetching: false,

    refreshProfile: async () => {
        const { user } = useAuth.getState();
        if (!user) return;

        // If a fetch is already in progress, wait for it instead of starting a new one
        if (activeFetchPromise) {
            return activeFetchPromise;
        }

        const fetchWithRetry = async (retries = 3): Promise<any> => {
            try {
                const { data, error } = await supabase
                    .from('user_auth_full')
                    .select('*')
                    .eq('id', user.id)
                    .maybeSingle();

                if (error) {
                    throw error;
                }
                return data;
            } catch (err: any) {
                // Retry specifically on AbortError (lock contention)
                if ((err.message?.includes('AbortError') || err.name === 'AbortError') && retries > 0) {
                    await new Promise(r => setTimeout(r, 500)); // Increased delay for retry
                    return fetchWithRetry(retries - 1);
                }
                throw err;
            }
        };

        activeFetchPromise = fetchWithRetry();
        set({ fetching: true });

        try {
            const data = await activeFetchPromise;
            set({
                profile: data ? {
                    id: data.id,
                    first_name: data.first_name,
                    last_name: data.last_name,
                    avatar_url: data.avatar_url,
                    bio: data.bio,
                    referred_by_id: data.referred_by_id,
                    role: data.role
                } : null,
                role: data?.role || null
            });
        } catch (err) {
            console.error('Error refreshing profile:', err);
        } finally {
            set({ fetching: false });
            activeFetchPromise = null;
        }
    },

    initialize: () => {
        if (isInitialized) return;
        isInitialized = true;

        const fetchUserData = async (user: User) => {
            if (activeFetchPromise) return activeFetchPromise;

            const fetchWithRetry = async (retries = 3): Promise<any> => {
                try {
                    const { data, error } = await supabase
                        .from('user_auth_full')
                        .select('*')
                        .eq('id', user.id)
                        .maybeSingle();

                    if (error) {
                        throw error;
                    }
                    return data;
                } catch (err: any) {
                    if ((err.message?.includes('AbortError') || err.name === 'AbortError') && retries > 0) {
                        await new Promise(r => setTimeout(r, 500)); // Increased delay for retry
                        return fetchWithRetry(retries - 1);
                    }
                    throw err;
                }
            };

            activeFetchPromise = fetchWithRetry();
            set({ fetching: true });

            try {
                const data = await activeFetchPromise;
                set({
                    profile: data ? {
                        id: data.id,
                        first_name: data.first_name,
                        last_name: data.last_name,
                        avatar_url: data.avatar_url,
                        bio: data.bio,
                        referred_by_id: data.referred_by_id,
                        role: data.role
                    } : null,
                    role: data?.role || null,
                    loading: false
                });
            } catch (err) {
                console.error('Error fetching user data from view:', err);
                set({ loading: false });
            } finally {
                set({ fetching: false });
                activeFetchPromise = null;
            }
        };

        supabase.auth.onAuthStateChange((event, session) => {
            const user = session?.user ?? null;

            if (!user) {
                set({ user: null, profile: null, role: null, loading: false, fetching: false });
            } else {
                set({ user });
                if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
                    // Uruchamiamy w tle (bez czekania awai) aby nie blokować Locka z gotrue-js!
                    fetchUserData(user).catch(console.error);
                } else {
                    set({ loading: false });
                }
            }
        });
    },

    signOut: async () => {
        try {
            await supabase.auth.signOut();
        } catch (err) {
            console.error('Sign out error:', err);
        }
        set({ user: null, profile: null, role: null, loading: false, fetching: false });
    }
}));
